import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

export default function Terms() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Terms of Service — DareDino";
  }, []);
  return (
    <div className="relative min-h-screen bg-[#050505] text-[#E2E8F0]">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-white text-xs uppercase tracking-widest"
          data-testid="terms-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="font-heading font-black text-lg text-white tracking-wider">
          DARE<span className="text-[#EAEB18]">DINO</span>{" "}
          <span className="text-[#00F0FF]">/ TERMS OF SERVICE</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 leading-relaxed" data-testid="terms-of-service-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-3">
          [ LAST UPDATED: 15 FEB 2026 ]
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl text-white mb-8">
          Terms of Service
        </h1>

        <div className="space-y-6 text-sm">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">1. Acceptance</h2>
            <p>
              By accessing or playing DareDino (&quot;the Service&quot;), you
              agree to these Terms of Service and our{" "}
              <button className="text-[#00F0FF] underline" onClick={() => navigate("/privacy-policy")}>
                Privacy Policy
              </button>
              . If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">2. Eligibility</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be at least 18 years old (or the legal age of majority in your country).</li>
              <li>You must be legally allowed to play skill-based games and make digital purchases in your jurisdiction.</li>
              <li>Employees of DareDino and their immediate families are not eligible for cash prizes in the India domestic tournament.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">3. Skill, Not Chance</h2>
            <p>
              DareDino is a <strong>skill-based reaction game</strong>. All
              outcomes depend entirely on the player&apos;s timing, reflexes,
              and strategy — never on luck, randomness, or wagering. No entry
              fee is required to compete on the global leaderboard.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              4. Leaderboard &amp; Private Rooms — Fair Use
            </h2>
            <p className="mb-2">
              The public daily / weekly leaderboards and the Private Rooms
              system exist to let players compete fairly and share the game
              with friends. The following actions constitute serious abuse and
              will trigger the sanctions listed in Section 5:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Using bots, macros, auto-clickers, remote-input scripts, or any tool that produces automated jumps or ducks.</li>
              <li>Injecting JavaScript, tampering with the game canvas, modifying network requests, replaying nonces, or forging HMAC signatures.</li>
              <li>Uploading fabricated scores that could not have been achieved by fair play (velocity, event-log, and duration checks are enforced server-side).</li>
              <li>Farming private-room codes to inflate the daily leaderboard, sharing accounts, or operating multiple accounts.</li>
              <li>Sharing, brute-forcing, or scraping room codes belonging to other users.</li>
              <li>Using DareDino to harass, threaten, or dox any other player.</li>
            </ul>
            <p className="mt-2">
              Scores earned inside a private room are <strong>not</strong>{" "}
              counted toward the global leaderboard and are not eligible for
              the India cash prize.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              5. Sanctions — Permanent Bans
            </h2>
            <p>
              Any confirmed instance of the abuse listed in Section 4 will
              result in:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Permanent, irreversible ban</strong> of the offending account.</li>
              <li>Forfeiture of any pending cash prize, DarePass entitlement, and unused subscription time — without refund.</li>
              <li>Removal of the account&apos;s scores from every current and historical leaderboard.</li>
              <li>Where fraud is suspected, escalation to Razorpay for chargeback dispute and to law enforcement where applicable.</li>
            </ul>
            <p className="mt-2">
              Ban decisions are final and made at DareDino&apos;s sole
              discretion based on server-side evidence.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              6. DarePass — Digital Microtransaction
            </h2>
            <p className="mb-2">
              <strong>DarePass</strong> is a paid digital add-on. A single
              monthly purchase unlocks the following benefits for the paid
              period:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>A completely <strong>ad-free experience</strong> across the entire site.</li>
              <li>A unique animated <strong>profile frame</strong> and shimmering name displayed on every leaderboard and inside every private room.</li>
              <li>Unlimited private-room creation (free users are capped at 3 rooms per day and must watch a rewarded ad to create each one).</li>
              <li>Access to the DarePass-only weekly tournament (where applicable).</li>
            </ul>
            <p className="mt-2">
              Pricing is currency-routed by the country your IP resolves to:
              users in India pay <strong>₹99 / month</strong> via Razorpay
              (UPI, netbanking, domestic cards), users outside India pay{" "}
              <strong>$2.99 / month</strong> via Razorpay&apos;s international
              checkout (international cards, Apple Pay, and other supported
              global methods).
            </p>
            <p className="mt-2">
              <strong>All digital purchases are final and non-refundable.</strong>{" "}
              By completing checkout you expressly consent to immediate
              digital delivery and you waive any statutory cooling-off period
              that would otherwise apply to digital content. Refunds are only
              considered if we materially fail to deliver a promised feature —
              in that specific case a pro-rated refund may be issued at our
              sole discretion.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              7. India Cash Prize (Domestic Only)
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>The daily / weekly cash-prize tournament is available <strong>only to players playing from India</strong>. Prizes are paid in INR via UPI to the ID you submit in your profile.</li>
              <li>Global users see a non-monetary global leaderboard experience. No cash prize is offered outside India.</li>
              <li>Winners are responsible for any taxes (including Indian TDS as per the Income Tax Act). PAN may be requested for high-value payouts.</li>
              <li>Payouts are processed within 7 working days of a leaderboard day closing.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              8. Account Termination
            </h2>
            <p>
              We may suspend or terminate any account for violations of these
              Terms, abuse of other players, or fraudulent behaviour. You may
              request voluntary account deletion at any time via{" "}
              <a href="mailto:privacy@daredino.com" className="text-[#00F0FF] underline">
                privacy@daredino.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">9. Disclaimers</h2>
            <p>
              The Service is provided &quot;as is&quot; without warranty of any
              kind. We do not guarantee uninterrupted access, error-free
              operation, or specific game outcomes. We are not affiliated with
              Google Inc. — DareDino is an independent creation inspired by
              the offline Chrome dinosaur Easter egg.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              10. Limitation of Liability
            </h2>
            <p>
              To the maximum extent allowed by law, our total liability for any
              claim arising from the Service is capped at the amount you paid
              us in the previous 6 months, or the equivalent of USD $10 for
              free-tier users.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">11. Governing Law</h2>
            <p>
              These Terms are governed by the laws of India. Any dispute is
              subject to the exclusive jurisdiction of the courts of Bengaluru,
              Karnataka.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">12. Changes</h2>
            <p>
              We may update these Terms; the &quot;Last Updated&quot; date will
              change. Continued use of the Service after an update constitutes
              acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">13. Contact</h2>
            <p>
              Legal:{" "}
              <a href="mailto:legal@daredino.com" className="text-[#00F0FF] underline">
                legal@daredino.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
